import { useState, useEffect, useCallback, useRef } from 'react'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import {
  adminGetSeasons,
  adminGetSeasonLeaderboardStatus,
  adminUploadSeasonSnapshot,
  adminClearSeasonSnapshot,
  adminTriggerSeasonPayout,
  adminGetRegistrationProgress,
  adminDownloadPayoutExport,
  adminGetPayoutConfig,
  adminSetPayoutConfig,
} from '../../../services/admin'

const REQUIRED_FIELDS = ['rank', 'email', 'username', 'score']

const DEFAULT_TIERS = [
  { minRank: 1, maxRank: 1, usdAmount: 20000 },
  { minRank: 2, maxRank: 2, usdAmount: 10000 },
  { minRank: 3, maxRank: 3, usdAmount: 5000 },
  { minRank: 4, maxRank: 10, usdAmount: 1500 },
  { minRank: 11, maxRank: 50, usdAmount: 400 },
  { minRank: 51, maxRank: 100, usdAmount: 150 },
  { minRank: 101, maxRank: 500, usdAmount: 20 },
]

function validateEntries(data) {
  if (!Array.isArray(data)) return 'Expected a JSON array of leaderboard rows.'
  if (data.length === 0) return 'The file has no rows.'
  const missing = REQUIRED_FIELDS.filter((f) => !(f in data[0]))
  if (missing.length) return `Rows are missing required field(s): ${missing.join(', ')}.`
  return null
}

function fmtDate(d) {
  if (!d) return '—'
  const date = new Date(d)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

export function SeasonLeaderboardManagement() {
  const [seasons, setSeasons] = useState([])
  const [loadingSeasons, setLoadingSeasons] = useState(true)
  const [seasonId, setSeasonId] = useState('')

  const [status, setStatus] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(false)

  const [progress, setProgress] = useState(null)
  const [exporting, setExporting] = useState(false)

  const [tiers, setTiers] = useState(DEFAULT_TIERS)
  const [cptPrice, setCptPrice] = useState('0.0077')
  const [configLoading, setConfigLoading] = useState(false)
  const [configSaving, setConfigSaving] = useState(false)
  const [configLoaded, setConfigLoaded] = useState(false)

  const [parsedEntries, setParsedEntries] = useState(null)
  const [parseError, setParseError] = useState(null)
  const [fileName, setFileName] = useState('')
  const [uploading, setUploading] = useState(false)

  const [confirmAction, setConfirmAction] = useState(null) // null | 'clear' | 'payout'
  const [actionLoading, setActionLoading] = useState(false)

  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    adminGetSeasons()
      .then((data) => {
        setSeasons(data)
        const active = data.find((s) => s.status === 'active') ?? data[0]
        if (active) setSeasonId(active.id)
      })
      .catch((err) => setError(err.message ?? 'Failed to load seasons'))
      .finally(() => setLoadingSeasons(false))
  }, [])

  const loadStatus = useCallback(async () => {
    if (!seasonId) return
    setLoadingStatus(true)
    setError(null)
    try {
      const [statusData, progressData] = await Promise.all([
        adminGetSeasonLeaderboardStatus(seasonId),
        adminGetRegistrationProgress(seasonId).catch(() => null),
      ])
      setStatus(statusData)
      setProgress(progressData)
    } catch (err) {
      setError(err.message ?? 'Failed to load snapshot status')
    } finally {
      setLoadingStatus(false)
    }
  }, [seasonId])

  useEffect(() => { loadStatus() }, [loadStatus])

  useEffect(() => {
    if (!seasonId) return
    setConfigLoading(true)
    setConfigLoaded(false)
    adminGetPayoutConfig(seasonId)
      .then((data) => {
        if (data?.payoutTiers?.length) setTiers(data.payoutTiers)
        else setTiers(DEFAULT_TIERS)
        if (data?.cptPriceUsd != null) setCptPrice(String(data.cptPriceUsd))
        else setCptPrice('0.0077')
      })
      .catch(() => { setTiers(DEFAULT_TIERS); setCptPrice('0.0077') })
      .finally(() => { setConfigLoading(false); setConfigLoaded(true) })
  }, [seasonId])

  const resetPicker = () => {
    setParsedEntries(null)
    setParseError(null)
    setFileName('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setParseError(null)
    setParsedEntries(null)
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result)
        const err = validateEntries(data)
        if (err) { setParseError(err); return }
        setParsedEntries(data)
      } catch {
        setParseError('That file is not valid JSON.')
      }
    }
    reader.readAsText(file)
  }

  const submitUpload = async () => {
    if (!parsedEntries || !seasonId) return
    setUploading(true)
    setError(null)
    try {
      await adminUploadSeasonSnapshot(seasonId, parsedEntries)
      setMessage(`Snapshot uploaded — ${parsedEntries.length} rows.`)
      setTimeout(() => setMessage(null), 4000)
      resetPicker()
      loadStatus()
    } catch (err) {
      setError(err.message ?? 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const confirmClear = async () => {
    setActionLoading(true)
    try {
      await adminClearSeasonSnapshot(seasonId)
      setMessage('Snapshot cleared.')
      setTimeout(() => setMessage(null), 4000)
      setConfirmAction(null)
      loadStatus()
    } catch (err) {
      setError(err.message ?? 'Failed to clear snapshot')
      setConfirmAction(null)
    } finally {
      setActionLoading(false)
    }
  }

  const confirmPayout = async () => {
    setActionLoading(true)
    try {
      await adminTriggerSeasonPayout(seasonId)
      setMessage('Payout triggered — Connect Wallet is now live on the /allocation page.')
      setTimeout(() => setMessage(null), 5000)
      setConfirmAction(null)
      loadStatus()
    } catch (err) {
      setError(err.message ?? 'Failed to trigger payout')
      setConfirmAction(null)
    } finally {
      setActionLoading(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    setError(null)
    try {
      await adminDownloadPayoutExport(seasonId)
    } catch (err) {
      setError(err.message ?? 'Failed to download export')
    } finally {
      setExporting(false)
    }
  }

  const updateTier = (i, field, value) => {
    setTiers((prev) => prev.map((t, idx) => idx === i ? { ...t, [field]: value === '' ? '' : Number(value) } : t))
  }

  const addTier = () => {
    setTiers((prev) => {
      const lastMax = prev.length ? Number(prev[prev.length - 1].maxRank) || 0 : 0
      return [...prev, { minRank: lastMax + 1, maxRank: lastMax + 1, usdAmount: 0 }]
    })
  }

  const removeTier = (i) => {
    setTiers((prev) => prev.filter((_, idx) => idx !== i))
  }

  const saveConfig = async () => {
    setConfigSaving(true)
    setError(null)
    try {
      const cleanTiers = tiers.map((t) => ({
        minRank: Number(t.minRank),
        maxRank: Number(t.maxRank),
        usdAmount: Number(t.usdAmount),
      }))
      await adminSetPayoutConfig(seasonId, cleanTiers, Number(cptPrice))
      setMessage('Payout structure saved — future uploads can omit cptReward and it will be computed automatically.')
      setTimeout(() => setMessage(null), 5000)
    } catch (err) {
      setError(err.message ?? 'Failed to save payout structure')
    } finally {
      setConfigSaving(false)
    }
  }

  const selectedSeason = seasons.find((s) => s.id === seasonId)

  return (
    <div className="flex flex-col gap-6">
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={(e) => { if (e.target === e.currentTarget) setConfirmAction(null) }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6" style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <h2 className="font-extrabold text-[14px] uppercase tracking-widest">
              {confirmAction === 'clear' ? 'Clear Snapshot?' : 'Trigger Payout?'}
            </h2>
            <p className="text-[13px] text-on-surface-variant">
              {confirmAction === 'clear'
                ? 'This removes all uploaded rows for this season so you can re-upload a corrected export. Users lose visibility until you upload again.'
                : 'This opens the Connect Wallet button on the /allocation page for every user with a row in this snapshot. Make sure the uploaded data is final before doing this.'}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmAction(null)} className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button
                onClick={confirmAction === 'clear' ? confirmClear : confirmPayout}
                disabled={actionLoading}
                className={`flex-1 py-2.5 font-bold text-[11px] uppercase tracking-widest transition-opacity disabled:opacity-50 ${confirmAction === 'clear' ? 'bg-error text-on-error' : 'bg-primary-container text-on-primary-fixed'}`}
              >
                {actionLoading ? 'Working…' : confirmAction === 'clear' ? 'Clear' : 'Trigger Payout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {message && <AdminAlert type="success" message={message} />}
      {error && <AdminAlert type="error" message={error} />}

      <div className="flex flex-col gap-1">
        <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Season</label>
        {loadingSeasons ? (
          <Skeleton className="h-11 w-full max-w-sm rounded" />
        ) : (
          <select
            value={seasonId}
            onChange={(e) => setSeasonId(e.target.value)}
            className="bg-surface border-2 border-on-background px-3 py-2.5 text-[13px] font-bold max-w-sm focus:outline-none focus:border-primary-container"
          >
            {seasons.length === 0 && <option value="">No seasons yet</option>}
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>{s.seasonName} {s.status ? `(${s.status})` : ''}</option>
            ))}
          </select>
        )}
      </div>

      {seasonId && (
        <>
          <div className="border-4 border-on-background bg-surface rounded-lg p-5 flex items-start justify-between gap-4 flex-wrap" style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
            {loadingStatus ? (
              <Skeleton className="h-16 w-full rounded" />
            ) : (
              <>
                <div className="flex flex-col gap-1">
                  <h3 className="font-bold text-[14px]">{selectedSeason?.seasonName ?? 'Season'}</h3>
                  <p className="text-[12px] text-on-surface-variant">
                    {status?.uploaded ? `${status.rowCount} rows uploaded · ${fmtDate(status.uploadedAt)}` : 'No snapshot uploaded yet'}
                  </p>
                  {progress && (
                    <p className="text-[12px] text-on-surface-variant">
                      {progress.registered} of {progress.eligible} eligible users have registered a payout wallet
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-2.5 py-1 border-2 rounded text-[10px] font-extrabold uppercase tracking-widest ${status?.claimOpen ? 'border-primary-container bg-primary-container/20 text-on-primary-container' : 'border-on-background/30 text-on-surface-variant'}`}>
                    {status?.claimOpen ? 'Claim Open' : 'Claim Closed'}
                  </span>
                  <button
                    onClick={() => setConfirmAction('payout')}
                    disabled={!status?.uploaded || status?.claimOpen}
                    className="px-4 py-2 bg-primary-container text-on-primary-fixed font-bold text-[10px] uppercase tracking-widest border-2 border-on-background hover:brightness-105 transition-all disabled:opacity-40"
                  >
                    Trigger Payout
                  </button>
                  <button
                    onClick={() => setConfirmAction('clear')}
                    disabled={!status?.uploaded}
                    className="px-4 py-2 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors disabled:opacity-40"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={!status?.claimOpen || exporting || !progress?.registered}
                    className="px-4 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-40"
                  >
                    {exporting ? 'Exporting…' : 'Export Payout CSV'}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="border-4 border-on-background bg-surface rounded-lg p-5 flex flex-col gap-4" style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
            <div>
              <h3 className="font-bold text-[14px]">Payout Structure</h3>
              <p className="text-[12px] text-on-surface-variant mt-1">
                Set once per season. Uploads can then omit <code className="font-mono">cptReward</code> per row — it's computed automatically from each row's rank against these tiers. Editable anytime; re-save and re-upload if numbers change.
              </p>
            </div>

            {configLoading ? (
              <Skeleton className="h-32 w-full rounded" />
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <label className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant whitespace-nowrap">$ per CPT</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={cptPrice}
                    onChange={(e) => setCptPrice(e.target.value)}
                    className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] font-bold w-32 focus:outline-none focus:border-primary-container"
                  />
                </div>

                <div className="border-2 border-on-background/20 rounded overflow-hidden">
                  <div className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 px-3 py-2 bg-surface-container-high text-[10px] font-extrabold uppercase tracking-widest text-on-surface-variant">
                    <span>Min Rank</span>
                    <span>Max Rank</span>
                    <span>$ Each</span>
                    <span></span>
                  </div>
                  {tiers.map((t, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 px-3 py-2 border-t border-on-background/10 items-center">
                      <input type="number" value={t.minRank} onChange={(e) => updateTier(i, 'minRank', e.target.value)}
                        className="bg-transparent border border-on-background/20 px-2 py-1.5 text-[12px] focus:outline-none focus:border-primary-container" />
                      <input type="number" value={t.maxRank} onChange={(e) => updateTier(i, 'maxRank', e.target.value)}
                        className="bg-transparent border border-on-background/20 px-2 py-1.5 text-[12px] focus:outline-none focus:border-primary-container" />
                      <input type="number" value={t.usdAmount} onChange={(e) => updateTier(i, 'usdAmount', e.target.value)}
                        className="bg-transparent border border-on-background/20 px-2 py-1.5 text-[12px] focus:outline-none focus:border-primary-container" />
                      <button onClick={() => removeTier(i)} className="text-error font-bold text-[16px] hover:opacity-70 transition-opacity" title="Remove tier">×</button>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <button onClick={addTier} className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                    + Add Tier
                  </button>
                  <button onClick={saveConfig} disabled={configSaving || !configLoaded}
                    className="px-5 py-2 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                    {configSaving ? 'Saving…' : 'Save Payout Structure'}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="border-4 border-on-background bg-surface rounded-lg p-5 flex flex-col gap-4" style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
            <div>
              <h3 className="font-bold text-[14px]">Upload Snapshot (JSON)</h3>
              <p className="text-[12px] text-on-surface-variant mt-1">
                Array of rows, each with at least <code className="font-mono">rank</code>, <code className="font-mono">email</code>, <code className="font-mono">username</code>, <code className="font-mono">score</code>. Omit <code className="font-mono">cptReward</code> to have it computed automatically from the Payout Structure above (set that first). email is what actually matches a row to a logged-in user (case-insensitive) — this supports seasons run on other systems where no Chicago News profileId exists at all. Uploading replaces the current snapshot for this season.
              </p>
            </div>

            <input ref={fileInputRef} type="file" accept="application/json,.json" onChange={handleFile}
              className="text-[12px] file:mr-3 file:px-4 file:py-2 file:border-2 file:border-on-background file:bg-surface-container file:font-bold file:text-[11px] file:uppercase file:tracking-widest file:cursor-pointer" />

            {parseError && <AdminAlert type="error" message={parseError} />}

            {parsedEntries && !parseError && (
              <div className="flex flex-col gap-3">
                <p className="text-[12px] font-bold text-on-surface-variant">
                  Parsed {fileName} — {parsedEntries.length} rows. Preview:
                </p>
                <div className="border-2 border-on-background/20 rounded overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead className="bg-surface-container-high">
                      <tr>
                        {Object.keys(parsedEntries[0]).map((k) => (
                          <th key={k} className="px-3 py-2 text-left font-bold uppercase tracking-widest text-[10px] text-on-surface-variant">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedEntries.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t border-on-background/10">
                          {Object.keys(parsedEntries[0]).map((k) => (
                            <td key={k} className="px-3 py-2 font-mono">{String(row[k])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-3">
                  <button onClick={resetPicker} className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                    Cancel
                  </button>
                  <button onClick={submitUpload} disabled={uploading}
                    className="px-5 py-2 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                    {uploading ? 'Uploading…' : `Upload ${parsedEntries.length} Rows`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}